// モバイルのボトムシート。高さは 3 つのスナップ位置だけを取り、ドラッグ中は
// inline style で追従させ、離した時点の位置と速度から最寄りのスナップへ戻す。
// スナップ後は inline style を捨て、高さの source of truth を CSS（data-state）に戻す。
//
// ドラッグ面はつまみだけでなくヘッダー全体（つまみ + タブ行）。pointer capture は使わず、
// pointerdown 後は window で pointermove / pointerup を追う。capture を取るとタブの click の
// target がヘッダーに化けてタブ切り替えが壊れるため。
export type SheetState = "peek" | "half" | "full";

export const SHEET_PEEK_HEIGHT = 96;
const SHEET_TOP_GAP = 64; // full 時に地図とトップバーを覗かせる余白
const HALF_RATIO = 0.5;
/** これを超える速度（px/ms）で離したら、位置ではなく向きでスナップ先を決める。 */
const FLICK_VELOCITY = 0.45;
/** この距離未満の移動はタップとして扱い、ドラッグを始めない。 */
const TAP_SLOP = 6;
const SNAP_ORDER: SheetState[] = ["peek", "half", "full"];

export interface SheetOptions {
  root: HTMLElement;
  /** ドラッグ面。つまみとタブ行を含むヘッダー */
  header: HTMLElement;
  /** タップで peek ⇄ half を切り替えるつまみ */
  handle: HTMLElement;
  onStateChange(state: SheetState): void;
}

function snapHeights(): Record<SheetState, number> {
  const vh = window.innerHeight;
  return {
    peek: SHEET_PEEK_HEIGHT,
    half: Math.round(vh * HALF_RATIO),
    full: Math.max(SHEET_PEEK_HEIGHT, vh - SHEET_TOP_GAP),
  };
}

function nearestState(height: number, velocity: number): SheetState {
  const heights = snapHeights();
  const current = SNAP_ORDER.reduce((best, s) =>
    Math.abs(heights[s] - height) < Math.abs(heights[best] - height) ? s : best,
  );
  if (Math.abs(velocity) < FLICK_VELOCITY) return current;

  // 上向きのドラッグ（velocity < 0）は「開く」方向
  const step = velocity < 0 ? 1 : -1;
  const next = SNAP_ORDER[Math.min(SNAP_ORDER.length - 1, Math.max(0, SNAP_ORDER.indexOf(current) + step))];
  return next ?? current;
}

export interface SheetController {
  setState(state: SheetState): void;
  getState(): SheetState;
}

export function createSheet(opts: SheetOptions): SheetController {
  const { root, header, handle, onStateChange } = opts;
  let state: SheetState = "peek";
  let pointerId: number | null = null;
  let dragged = false;
  let suppressClick = false;
  let startY = 0;
  let startHeight = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  function setState(next: SheetState): void {
    state = next;
    root.dataset.state = next;
    root.style.removeProperty("height");
    onStateChange(next);
  }

  function onMove(ev: PointerEvent): void {
    if (ev.pointerId !== pointerId) return;
    const dy = ev.clientY - startY;
    if (!dragged) {
      if (Math.abs(dy) < TAP_SLOP) return;
      dragged = true;
      root.classList.add("is-dragging");
    }
    const heights = snapHeights();
    root.style.height = `${Math.min(heights.full, Math.max(heights.peek, startHeight - dy))}px`;

    const dt = ev.timeStamp - lastTime;
    if (dt > 0) velocity = (ev.clientY - lastY) / dt;
    lastY = ev.clientY;
    lastTime = ev.timeStamp;
  }

  function onEnd(ev: PointerEvent): void {
    if (ev.pointerId !== pointerId) return;
    pointerId = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onEnd);
    window.removeEventListener("pointercancel", onEnd);
    if (!dragged) return; // タップはこの後の click に任せる（つまみ = 開閉、タブ = 切り替え）

    root.classList.remove("is-dragging");
    // ドラッグ直後に飛んでくる click でタブが切り替わらないようにする
    suppressClick = true;
    setState(nearestState(root.getBoundingClientRect().height, velocity));
  }

  header.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (!ev.isPrimary) return;
    // 前回の pointerup が来ないまま終わっていても、新しいポインタで必ず取り直す
    pointerId = ev.pointerId;
    dragged = false;
    suppressClick = false;
    startY = ev.clientY;
    lastY = ev.clientY;
    lastTime = ev.timeStamp;
    velocity = 0;
    startHeight = root.getBoundingClientRect().height;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  });

  header.addEventListener(
    "click",
    (ev) => {
      if (!suppressClick) return;
      suppressClick = false;
      ev.preventDefault();
      ev.stopPropagation();
    },
    true,
  );

  handle.addEventListener("click", () => setState(state === "peek" ? "half" : "peek"));

  root.dataset.state = state;

  return {
    setState,
    getState: () => state,
  };
}
